// Companion/useChatBlockThreadDefault.ts
//
// "Always for my blocks" in the composer pill's menu and "Keep one chat for
// each question block" in Settings → AI Companion: one profile field
// (`chatBlockThreadDefault`, ORA-1285), saved through the same profile update
// as haptics. The switch moves at once; a failed save puts it back.

import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import { updateCurrentUserInfo } from '@/services/auth';
import { userUpdateReceived } from '@/slices/user/userSlice';
import type { AppDispatch, RootState } from '@/store/store';
import {
  captureBlockScopeChange,
  type ChatBlockScopeVia,
} from './chatBlockScopeActions';

const SAVE_FAILED = 'That setting could not be saved. Please try again.';

export const selectChatBlockThreadDefault = (state: RootState): boolean =>
  state.user.profile?.chatBlockThreadDefault === true;

export const useChatBlockThreadDefault = () => {
  const dispatch = useDispatch<AppDispatch>();
  const isOn = useSelector(selectChatBlockThreadDefault);
  const [isSaving, setIsSaving] = useState(false);

  const save = useCallback(
    async (
      next: boolean,
      via: Extract<ChatBlockScopeVia, 'settings' | 'always'>
    ) => {
      dispatch(userUpdateReceived({ chatBlockThreadDefault: next }));
      setIsSaving(true);

      const updated = await updateCurrentUserInfo({
        chatBlockThreadDefault: next,
      });

      setIsSaving(false);

      if (!updated) {
        dispatch(userUpdateReceived({ chatBlockThreadDefault: !next }));
        enqueueSnackbar(SAVE_FAILED, { variant: 'error' });
        return false;
      }

      captureBlockScopeChange(
        next ? 'question' : 'block',
        next ? 'block' : 'question',
        via
      );

      return true;
    },
    [dispatch]
  );

  return { isOn, isSaving, save };
};
